import fetchDisplayedLogo from '@Application/Branding/DisplayedLogo/fetchDisplayedLogo';
import readDisplayedLogo from '@Application/Branding/DisplayedLogo/readDisplayedLogo';
import recordDisplayedLogo from '@Application/Branding/DisplayedLogo/recordDisplayedLogo';
import FetchDisplayedLogo from '@Application/Branding/DisplayedLogo/Queries/FetchDisplayedLogo';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

/**
 * The logo shown in the header and the drawer: the one fetched for the current structure, else the
 * copy memorized on this device (read once per mount), else `null`. A failed or pending fetch never
 * surfaces: the fallback is returned silently.
 */
const useDisplayedLogo = (): string | null => {
	const dispatch = useDispatch();
	const { user } = useAuthenticatedUser();
	const { currentOrganization, hasConfirmedNoOrganizations } = useCurrentOrganization();

	const accountId = user?.account?.id ?? null;
	const organizationId = currentOrganization?.id ?? null;

	const memorizedLogo = React.useMemo(readDisplayedLogo, []);

	const { data } = useStoreQuery({
		selector: (state) => state.brandingStore.displayedLogo,
		stopOn: (value) => value !== null && value.ownerId === organizationId,
		query: () => {
			if (accountId === null || organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchDisplayedLogo(new FetchDisplayedLogo(organizationId)));
		},
		deps: [accountId, organizationId],
	});

	React.useEffect(() => {
		if (hasConfirmedNoOrganizations) {
			recordDisplayedLogo(null);
		}
	}, [hasConfirmedNoOrganizations]);

	return React.useMemo(() => {
		if (hasConfirmedNoOrganizations) {
			return null;
		}

		if (data !== null && data.ownerId === organizationId) {
			return data.dataUrl;
		}

		return memorizedLogo;
	}, [hasConfirmedNoOrganizations, data, organizationId, memorizedLogo]);
};

export default useDisplayedLogo;
